// src/app/modules/user/modules/accounts/components/patient/BillingSection.tsx
import React from 'react'
import {CircularProgressbar, buildStyles} from 'react-circular-progressbar'
import {FaCheck, FaTimes} from 'react-icons/fa'
import 'react-circular-progressbar/dist/styles.css'
import {usePatientBillingReport} from '../../../../hooks/usePatientBillingReport'
import {formatBillingCycleDateToUser} from '../../helper/dateTimeHelpers'

const donutContainerStyle: React.CSSProperties = {
  width: 72,
  height: 72,
}

const assignedCircleStyle: React.CSSProperties = {
  ...donutContainerStyle,
  borderRadius: '50%',
  backgroundColor: '#E6F5E8',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

const titleStyle: React.CSSProperties = {
  fontSize: '1.8rem',
  fontWeight: 600,
  color: '#333',
}

const subtitleStyle: React.CSSProperties = {
  fontSize: '0.875rem',
  color: '#777',
  marginTop: '0.25rem',
}

interface AssignedCardProps {
  cpt: string
  subtitle: string
  assigned: boolean
}
const AssignedCard: React.FC<AssignedCardProps> = ({cpt, subtitle, assigned}) => (
  <div className='custom-card card-100 d-flex align-items-center'>
    <div style={assignedCircleStyle}>
      {assigned ? <FaCheck size={24} color='#2E7D62' /> : <FaTimes size={24} color='#D32F2F' />}
    </div>
    <div>
      <div style={titleStyle}>CPT {cpt}</div>
      <div style={subtitleStyle}>{subtitle}</div>
    </div>
  </div>
)

type DonutVariant = 'default' | 'warning' | 'na'

const donutColors: Record<DonutVariant, {path: string; trail: string; text: string}> = {
  default: {path: '#2E7D62', trail: '#E6F5E8', text: '#2E7D62'},
  warning: {path: '#e67e22', trail: '#fef3e2', text: '#e67e22'},
  na: {path: '#ccc', trail: '#f0f0f0', text: '#999'},
}

interface DonutCardProps {
  cpt: string
  percentage: number
  subtitle: string
  displayText?: string
  variant?: DonutVariant
}

const DonutCard: React.FC<DonutCardProps> = ({cpt, percentage, subtitle, displayText, variant = 'default'}) => {
  const colors = donutColors[variant]
  return (
    <div className='custom-card card-100 d-flex align-items-center'>
      <div style={donutContainerStyle}>
        <CircularProgressbar
          value={percentage}
          text={displayText ?? `${Math.round(percentage)}%`}
          strokeWidth={8}
          styles={buildStyles({
            pathColor: colors.path,
            trailColor: colors.trail,
            textColor: colors.text,
            textSize: '24px',
          })}
        />
      </div>
      <div>
        <div style={titleStyle}>CPT {cpt}</div>
        <div style={subtitleStyle}>{subtitle}</div>
      </div>
    </div>
  )
}

interface UnitCardProps {
  cpt: string
  units: number
  subtitle: string
}

const UnitCard: React.FC<UnitCardProps> = ({cpt, units, subtitle}) => (
  <div className='custom-card card-100 d-flex align-items-center'>
    <div style={donutContainerStyle}>
      <CircularProgressbar
        value={100}
        text={units.toString().padStart(2, '0')}
        strokeWidth={8}
        styles={buildStyles({
          pathColor: '#2E7D62',
          trailColor: '#E6F5E8',
          textColor: '#2E7D62',
          textSize: '24px',
        })}
      />
    </div>
    <div>
      <div style={titleStyle}>CPT {cpt}</div>
      <div style={subtitleStyle}>{subtitle}</div>
    </div>
  </div>
)

interface BillingSectionProps {
  patientUUID: string
  monthYear: string
  readingsThreshold?: number
  timeThreshold?: number
  addonTimeThreshold?: number
}

const BillingSection: React.FC<BillingSectionProps> = ({
  patientUUID,
  monthYear,
  // TODO:: these should be in config
  readingsThreshold = 16,
  timeThreshold = 20,
  addonTimeThreshold = 20,
}) => {
  const {latestCycle, loading, error} = usePatientBillingReport(patientUUID, monthYear)

  if (loading) return <div>Loading...</div>
  if (error) return <div>Error: {error}</div>

  const report = latestCycle || {
    cycle_start: null,
    cycle_end: null,
    status: null,
    care_minutes: '0',
    readings: 0,
    cpt_99453: false,
    cpt_99454: false,
    cpt_99445: false,
    cpt_99457: false,
    cpt_99458: 0,
    cpt_99470: false,
  }

  // Cycle dates
  let cycleStart: string | null = null
  let cycleEnd: string | null = null

  if (report.cycle_start && report.cycle_end) {
    cycleStart = formatBillingCycleDateToUser(report.cycle_start)
    cycleEnd = formatBillingCycleDateToUser(report.cycle_end)
  }

  // --- Device & Readings group ---

  // 99453: device assigned
  const assignedSubtitle = (report.cpt_99453 ? 'Device' : 'No device') + ' assigned this cycle'

  // 99445: short monitoring (2-15 reading days, requires provider review)
  const readingsCount = report.readings ?? 0
  const cpt99445MinThreshold = 2
  const cpt99445MaxThreshold = 15
  const cpt99445IsNA = readingsCount > cpt99445MaxThreshold
  const cpt99445Percent = cpt99445IsNA
    ? 0
    : Math.min((readingsCount / cpt99445MinThreshold) * 100, 100)

  let cpt99445Subtitle: string
  if (readingsCount < cpt99445MinThreshold) {
    const remaining = cpt99445MinThreshold - readingsCount
    cpt99445Subtitle = `${remaining} more day${remaining !== 1 ? 's' : ''} needed`
  } else if (readingsCount <= cpt99445MaxThreshold) {
    cpt99445Subtitle = `${readingsCount} days (requires review)*`
  } else {
    cpt99445Subtitle = 'Exceeds 15 readings (use 99454)'
  }

  // 99454: readings threshold (16+ days)
  const readingsPercent = Math.min((readingsCount / readingsThreshold) * 100, 100)
  const readingsRemaining = Math.max(readingsThreshold - readingsCount, 0)

  // --- Care Management group ---

  // 99470: initial care management (10-19 minutes)
  const totalMinutes = parseFloat(report.care_minutes) || 0
  const cpt99470MinThreshold = 10
  const cpt99470IsNA = totalMinutes >= timeThreshold // 99470 N/A when 99457 applies (≥20 min)
  const cpt99470Percent = cpt99470IsNA
    ? 0
    : Math.min((totalMinutes / cpt99470MinThreshold) * 100, 100)

  let cpt99470Subtitle: string
  if (totalMinutes < cpt99470MinThreshold) {
    const remaining = Math.max(cpt99470MinThreshold - totalMinutes, 0).toFixed(1)
    cpt99470Subtitle = `${remaining} more min to bill`
  } else if (!cpt99470IsNA) {
    cpt99470Subtitle = `${totalMinutes.toFixed(1)} min (10-19 min)`
  } else {
    cpt99470Subtitle = 'Use 99457 (≥20 min)'
  }

  // 99457: base time threshold (20+ minutes)
  const timePercent = Math.min((totalMinutes / timeThreshold) * 100, 100)
  const timeRemaining = Math.max(timeThreshold - totalMinutes, 0).toFixed(1)

  // 99458: add-on units (max 3)
  const extraMinutes = Math.max(totalMinutes - timeThreshold, 0)
  const timeAddonPercent = Math.min((extraMinutes / addonTimeThreshold) * 100, 100)
  const timeAddonRemaining = Math.max(addonTimeThreshold - extraMinutes, 0).toFixed(1)

  const maxUnits = 3
  const calculatedUnits = Math.floor(extraMinutes / addonTimeThreshold)
  const addonUnits = Math.min(calculatedUnits, maxUnits)

  const nextUnitProgress = extraMinutes % addonTimeThreshold
  const addonRemaining = Math.max(addonTimeThreshold - nextUnitProgress, 0).toFixed(1)

  const addonSubtitle =
    addonUnits === maxUnits
      ? 'Maximum units billed'
      : `${addonRemaining} more mins to bill second unit`

  const displayDate = cycleStart && cycleEnd ? `(${cycleStart} - ${cycleEnd})` : null

  return (
    <div className='bg-white border rounded-4 my-4' style={{gap: '1rem', padding: '2rem'}}>
      <div className='d-flex align-items-center gap-2'>
        <h3>Compliance</h3>
        <span className='text-muted'>{displayDate}</span>
        {latestCycle?.status === 'completed' && <span className='badge bg-warning text-dark ms-2'>Last Cycle</span>}
      </div>

      {!latestCycle ? (
        <div className='text-center py-4'>
          <div className='text-muted fs-5 fw-medium'>
            Assign device to patient to see Compliance
          </div>
        </div>
      ) : (
        <>
          {/* Device & Readings */}
          <h6 className='text-secondary fw-semibold mt-4 mb-2'>Device &amp; Readings</h6>
          <div className='row g-3'>
            <div className='col-lg-4 col-md-6 col-12 px-2'>
              <AssignedCard cpt='99453' subtitle={assignedSubtitle} assigned={report.cpt_99453 || false} />
            </div>
            <div className='col-lg-4 col-md-6 col-12 px-2'>
              <DonutCard
                cpt='99445'
                percentage={cpt99445Percent}
                subtitle={cpt99445Subtitle}
                displayText={cpt99445IsNA ? 'N/A' : undefined}
                variant={cpt99445IsNA ? 'na' : readingsCount >= cpt99445MinThreshold ? 'warning' : 'default'}
              />
            </div>
            <div className='col-lg-4 col-md-6 col-12 px-2'>
              <DonutCard
                cpt='99454'
                percentage={readingsPercent}
                subtitle={`${readingsRemaining} more readings to bill`}
              />
            </div>
          </div>

          {/* Care Management */}
          <h6 className='text-secondary fw-semibold mt-4 mb-2'>Care Management</h6>
          <div className='row g-3'>
            <div className='col-lg-4 col-md-6 col-12 px-2'>
              <DonutCard
                cpt='99470'
                percentage={cpt99470Percent}
                subtitle={cpt99470Subtitle}
                displayText={cpt99470IsNA ? 'N/A' : undefined}
                variant={cpt99470IsNA ? 'na' : 'default'}
              />
            </div>
            <div className='col-lg-4 col-md-6 col-12 px-2'>
              <DonutCard
                cpt='99457'
                percentage={timePercent}
                subtitle={`${timeRemaining} more minutes to bill`}
              />
            </div>
            <div className='col-lg-4 col-md-6 col-12 px-2'>
              {extraMinutes < addonTimeThreshold ? (
                <DonutCard
                  cpt='99458'
                  percentage={timeAddonPercent}
                  subtitle={`${timeAddonRemaining} more minutes to bill one unit`}
                />
              ) : (
                <UnitCard cpt='99458' units={addonUnits} subtitle={addonSubtitle} />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default BillingSection
